import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { submitLead } from "@/lib/api";
import { SERVICE_OPTIONS } from "@/data/content";
import { toast } from "sonner";

const STEPS = ["Personal Details", "Service Required", "Business Profile", "Proposed Plan", "Consent"];

const initial = {
  full_name: "", email: "", phone: "", nationality: "", residence_country: "", preferred_language: "",
  service_required: "", service_key: null, country_key: null,
  current_business: "", industry: "", years_in_business: "", revenue_range: "", countries_served: "", business_website: "",
  target_country: "", proposed_activity: "", preferred_timeline: "", plan_relocate: "", family_members: "", message: "",
  consent: false,
};

const inputCls = "w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-brand focus:ring-1 focus:ring-emerald-brand transition-colors";
const labelCls = "block text-sm font-semibold text-navy mb-1.5";

export default function AssessmentForm({ compact = false, defaultService = "" }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ ...initial, service_required: defaultService });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setField = (k) => (e) => set(k, e.target.value);

  const pickService = (opt) => setData((d) => ({ ...d, service_required: opt.label, service_key: opt.key, country_key: opt.country || null }));

  const canNext = () => {
    if (step === 0) return data.full_name && /.+@.+\..+/.test(data.email);
    if (step === 1) return !!data.service_required;
    if (step === 4) return data.consent;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await submitLead({ ...data, form_name: "Lead Assessment" });
      setResult(res);
      toast.success("Assessment submitted successfully.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally { setSubmitting(false); }
  };

  if (result) {
    return (
      <div data-testid="assessment-confirmation" className="text-center py-10 px-6">
        <div className="w-16 h-16 rounded-full bg-emerald-brand/10 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="text-emerald-brand" size={36} />
        </div>
        <h3 className="text-2xl font-bold text-navy">Thank You</h3>
        <p className="text-slate-600 mt-2">Your confidential assessment has been received.</p>
        <p className="mt-3 text-sm">Your reference number is <span className="font-bold text-navy" data-testid="lead-id">{result.lead_id}</span></p>
        <p className="text-slate-500 text-sm mt-4 max-w-md mx-auto">A member of our advisory team will be in touch. The appropriate structure depends on your business and circumstances. Contact us to know more.</p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/contact" className="btn-gold rounded-full px-6 py-3 font-bold">Book a Consultation</Link>
          <Link to="/insights" className="rounded-full px-6 py-3 font-semibold border-2 border-navy text-navy hover:bg-navy hover:text-white transition-colors">Explore Insights</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="assessment-form-container" className="w-full">
      {/* Progress */}
      <div className="flex items-center gap-1.5 mb-6">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-gold" : "bg-slate-200"}`} />
            {!compact && <div className={`mt-2 text-[11px] font-semibold ${i === step ? "text-navy" : "text-slate-400"} hidden md:block`}>{s}</div>}
          </div>
        ))}
      </div>
      <div className="text-xs font-bold tracking-wide uppercase text-emerald-brand mb-4">Step {step + 1} of 5 — {STEPS[step]}</div>

      {step === 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 fade-up">
          <div className="sm:col-span-2"><label className={labelCls}>Full Name *</label><input data-testid="assess-full-name" className={inputCls} value={data.full_name} onChange={setField("full_name")} /></div>
          <div><label className={labelCls}>Email *</label><input data-testid="assess-email" className={inputCls} value={data.email} onChange={setField("email")} /></div>
          <div><label className={labelCls}>Phone or WhatsApp</label><input data-testid="assess-phone" className={inputCls} value={data.phone} onChange={setField("phone")} /></div>
          <div><label className={labelCls}>Nationality</label><input className={inputCls} value={data.nationality} onChange={setField("nationality")} /></div>
          <div><label className={labelCls}>Current Country of Residence</label><input className={inputCls} value={data.residence_country} onChange={setField("residence_country")} /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Preferred Language</label><input className={inputCls} value={data.preferred_language} onChange={setField("preferred_language")} /></div>
        </div>
      )}

      {step === 1 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 fade-up">
          {SERVICE_OPTIONS.map((opt) => (
            <button key={opt.label} data-testid={`assess-service-${opt.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} type="button" onClick={() => pickService(opt)}
              className={`text-left rounded-xl border-2 px-4 py-3.5 text-sm font-semibold transition-all ${data.service_required === opt.label ? "border-emerald-brand bg-emerald-brand/5 text-navy" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {step === 2 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 fade-up">
          <div><label className={labelCls}>Current Business or Occupation</label><input className={inputCls} value={data.current_business} onChange={setField("current_business")} /></div>
          <div><label className={labelCls}>Industry</label><input className={inputCls} value={data.industry} onChange={setField("industry")} /></div>
          <div><label className={labelCls}>Years in Business</label><input className={inputCls} value={data.years_in_business} onChange={setField("years_in_business")} /></div>
          <div><label className={labelCls}>Countries Currently Served</label><input className={inputCls} value={data.countries_served} onChange={setField("countries_served")} /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Business Website (if available)</label><input className={inputCls} value={data.business_website} onChange={setField("business_website")} /></div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Business Profile</label>
            <input className={inputCls} placeholder="Please contact us to discuss your business profile." value={data.revenue_range} onChange={setField("revenue_range")} />
            <p className="text-xs text-slate-400 mt-1">We do not ask for exact revenue, investment amounts or budgets.</p>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 fade-up">
          <div><label className={labelCls}>Target Country</label><input className={inputCls} value={data.target_country} onChange={setField("target_country")} /></div>
          <div><label className={labelCls}>Proposed Activity</label><input className={inputCls} value={data.proposed_activity} onChange={setField("proposed_activity")} /></div>
          <div><label className={labelCls}>Preferred Timeline</label><input className={inputCls} value={data.preferred_timeline} onChange={setField("preferred_timeline")} /></div>
          <div>
            <label className={labelCls}>Do you plan to relocate?</label>
            <select className={inputCls} value={data.plan_relocate} onChange={setField("plan_relocate")}>
              <option value="">Select</option><option>Yes</option><option>No</option><option>Not sure yet</option>
            </select>
          </div>
          <div><label className={labelCls}>Accompanying family members (if relevant)</label><input className={inputCls} value={data.family_members} onChange={setField("family_members")} /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Message</label><textarea rows={3} className={inputCls} value={data.message} onChange={setField("message")} /></div>
        </div>
      )}

      {step === 4 && (
        <div className="fade-up">
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-5 flex gap-3">
            <ShieldCheck className="text-emerald-brand shrink-0" />
            <label className="text-sm text-slate-700 leading-relaxed flex gap-3 cursor-pointer">
              <input data-testid="assess-consent" type="checkbox" checked={data.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1 w-5 h-5 accent-emerald-700 shrink-0" />
              <span>I understand that submitting this form does not create a professional-client relationship or guarantee company registration, bank account opening, work-permit approval, visa approval, permanent residence or any tax result. See our <Link to="/legal/privacy-policy" className="text-emerald-brand font-semibold underline">Privacy Policy</Link>.</span>
            </label>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mt-8">
        <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-slate-500 hover:text-navy disabled:opacity-0">
          <ArrowLeft size={16} /> Back
        </button>
        {step < 4 ? (
          <button data-testid="assessment-next-btn" type="button" onClick={() => canNext() && setStep((s) => s + 1)} disabled={!canNext()}
            className="inline-flex items-center gap-2 btn-gold rounded-full px-7 py-3 font-bold disabled:opacity-50">
            Continue <ArrowRight size={16} />
          </button>
        ) : (
          <button data-testid="assessment-submit-btn" type="button" onClick={submit} disabled={!canNext() || submitting}
            className="inline-flex items-center gap-2 btn-gold rounded-full px-7 py-3 font-bold disabled:opacity-50">
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : <>Submit Assessment <CheckCircle2 size={16} /></>}
          </button>
        )}
      </div>
    </div>
  );
}
